import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { first, isUndefined, last, mapValues, omitBy, reverse } from "lodash";
import { forkJoin, map, Observable, switchMap } from "rxjs";
import {
  Alarm,
  AlarmEvent,
  AlarmEventLevel
} from "src/app/backend/models/alarm";
import { ArchiveBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/archive.backend";
import { BackupBackend } from "src/app/modules/bootstrap/rocket-ui/backend/api/backup/backup.backend";
import { Archive } from "src/app/modules/bootstrap/rocket-ui/backend/models/archive";
import { dateUtil } from "src/app/support/date-util";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { sntTranslate } from "src/app/support/translate";
import { environment } from "src/environments/environment";

@Injectable()
export class AlarmBackend {
  private readonly http = inject(HttpClient);

  private readonly archiveBackend = inject(ArchiveBackend);
  private readonly backupBackend = inject(BackupBackend);

  load(id: string): Observable<Alarm> {
    return this.http
      .get<Alarm>(`${environment.snoots.api.notification}/alarms/${id}`)
      .pipe(map((alarm: Alarm) => extend(alarm)));
  }

  search(request: PageRequest): Observable<PaginatedResult<Alarm>> {
    return this.http
      .get<PaginatedResult<Alarm>>(
        `${environment.snoots.api.notification}/alarms`,
        {
          params: <any>omitBy(
            {
              p: request.page,
              s: request.pageSize,
              ...request.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => {
          response.docs = response.docs.map(extend);

          return response;
        })
      );
  }

  listOpen(): Observable<Alarm[]> {
    return this.search({
      filterValues: {
        closed: false
      }
    }).pipe(
      map((response) => {
        return response.docs;
      })
    );
  }

  listSortByClosedAt(request: PageRequest): Observable<PaginatedResult<Alarm>> {
    return this.search({
      page: request.page,
      pageSize: request.pageSize,
      filterValues: {
        ...request.filterValues,
        sort: "desc",
        orderBy: "createdAt"
      }
    });
  }

  listSortByCreatedAtWithResourceName(
    request: PageRequest
  ): Observable<PaginatedResult<Alarm>> {
    return forkJoin({
      archives: this.archiveBackend.loadAll(),
      backups: this.backupBackend.search({})
    }).pipe(
      switchMap(({ archives, backups }) => {
        return this.listSortByClosedAt(request).pipe(
          map((response: PaginatedResult<Alarm>) => {
            response.docs.forEach((alarm: Alarm) => {
              switch (alarm.resource.type) {
                case "archive": {
                  alarm.resource.archive = archives.docs.find(
                    (x: Archive) => x.id === alarm.resource.id
                  );

                  break;
                }
                case "backup": {
                  alarm.resource.backup = backups.docs.find(
                    (x) => x.id === alarm.resource.id
                  );

                  break;
                }
              }
            });

            return response;
          })
        );
      })
    );
  }
}

function extend(alarm: Alarm): Alarm {
  alarm.closed = !!alarm.closedAt;

  if (!alarm.events) {
    alarm.events = [];
  }

  alarm.events.forEach((event: AlarmEvent) => {
    event.message.label = sntTranslate(
      last(event.message.key.split(":"))!,
      mapValues(event.message.params, (paramValue: string) => {
        return sntTranslate(paramValue);
      })
    );
  });

  const mostReventEvent = first(
    reverse(
      (alarm.events || []).sort(
        (leftEvent: AlarmEvent, rightEvent: AlarmEvent) => {
          return (
            dateUtil(leftEvent.createdAt).toDate().getTime() -
            dateUtil(rightEvent.createdAt).toDate().getTime()
          );
        }
      )
    )
  );

  if (mostReventEvent) {
    alarm.level = mostReventEvent.level;
    alarm.mostRecentEvent = mostReventEvent;
  } else {
    alarm.level = AlarmEventLevel.NONE;
  }

  return alarm;
}
